import { FC, useEffect, useState } from 'react';
import { Grid as KendoGrid, GridColumn, GridNoRecords, GridCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { State as ODataState, process, DataResult } from '@progress/kendo-data-query';
import api from '../../api/Api';
import { CircularProgress } from '@mui/material';
import { defaultCustomerContactsModel, CustomerContactsModel } from 'src/types/CustomerContactsModel';
import { Customer } from 'src/types/customer';
import PhoneNumberCell from '../grid/PhoneNumberCell';
import EmailCell from '../grid/EmailCell';

interface Props {
  dataItem: Customer;
}

const CustomerContactsDetail: FC<React.PropsWithChildren<Props>> = ({ dataItem }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contacts, setContacts] = useState<CustomerContactsModel[]>([{ ...defaultCustomerContactsModel }]);
  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: [],
      logic: 'and',
    },
    skip: 0,
    take: 20,
  });
  const [processedContacts, setProcessedContacts] = useState<DataResult>({ data: [], total: 0 });

  function loadContacts(customer: Customer) {
    if (!customer || !customer.number) {
      setContacts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    api
      .get<CustomerContactsModel[]>(`/customer/${customer.number}/contacts`)
      .then((contactList: CustomerContactsModel[]) => {
        setContacts(contactList || []);
      })
      .catch((error) => {
        console.error(error);
        setContacts([]);
      })
      .finally(() => setIsLoading(false));
  }

  const handleGridDataStateChange = (e: GridDataStateChangeEvent) => {
    setGridDataState(e.dataState);
  };

  useEffect(() => {
    if (dataItem && dataItem.number) {
      loadContacts(dataItem);
    } else {
      setContacts([]); // Clear contacts if dataItem is not valid
    }
  }, [dataItem]);

  useEffect(() => {
    setProcessedContacts(process(contacts, gridDataState));
  }, [contacts, gridDataState]);

  const NameCellComponent = (props: GridCellProps) => (
    <span>
      {props.dataItem.firstName} {props.dataItem.lastName}
    </span>
  );

  const AddressCellComponent = (props: GridCellProps) => {
    const { address1, city, state, zipcode } = props.dataItem;
    const fullAddress = [address1, city, state, zipcode].filter(Boolean).join(', ');
    return <span>{fullAddress}</span>;
  };

  const EmailCellComponent = (props: GridCellProps) => <EmailCell {...props} linked />;
  const PhoneCellComponent = (props: GridCellProps) => <PhoneNumberCell {...props} linked />;
  const FaxCellComponent = (props: GridCellProps) => <PhoneNumberCell {...props} />;

  return (
    <KendoGrid data={processedContacts} {...gridDataState} onDataStateChange={handleGridDataStateChange}>
      <GridNoRecords>{isLoading ? <CircularProgress /> : 'No records available.'}</GridNoRecords>
      <GridColumn title="Name" cells={{ data: NameCellComponent }} width={170}></GridColumn>
      <GridColumn title="Title" field="title" width={150}></GridColumn>
      <GridColumn title="Email" field="email" cells={{ data: EmailCellComponent }} width={280}></GridColumn>
      <GridColumn title="Phone" field="phoneNumber" width={130} cells={{ data: PhoneCellComponent }}></GridColumn>
      <GridColumn title="Address" cells={{ data: AddressCellComponent }} width={170}></GridColumn>
      <GridColumn title="Fax" field="faxNumber" width={100} cells={{ data: FaxCellComponent }}></GridColumn>
      <GridColumn title="ID" field="contactId" width={100}></GridColumn>
      <GridColumn title="" field=""></GridColumn>
    </KendoGrid>
  );
};

export default CustomerContactsDetail;
